import {
  CardNames,
  encodeWSRequest,
  WSActionTypes,
  wsActionTypes,
  WSFightData,
  WSMessage,
  WSMessageStatus
} from 'shared';
import localStorage from '../../utils/localStorage';
import { nanoid } from 'nanoid';
import useWs from '../../contexts/websockets';
import { joinWaitingRoomHandler } from './handlers/joinWaitingRoomHandler';
import { makeMoveHandler } from './handlers/makeMoveHandler';
import { getFightDataHandler } from './handlers/getFightDataHandler';
import { giveUpHandler } from './handlers/giveUpHandler';
import { exitHandler } from './handlers/exitHandler';

export type WSMessageResolver<T> = (data: T) => any;
export type WSMessageRejector = (data: {
  error: any;
  status: WSMessageStatus;
}) => any;

export type WSError = {
  error: string;
  status?: number;
};

type WSMessageHandler<T> = (
  parsed: WSMessage<T>,
  resolve: WSMessageResolver<T>,
  reject: WSMessageRejector
) => any;

interface SendMessageResult<T> {
  promise: Promise<T>;
  reqId: string;
  resolve: (data: T) => any;
  reject: (data: any) => any;
}

interface Requests {
  [key: string]: {
    resolve: WSMessageResolver<any>;
    reject: WSMessageRejector;
    reqId: string;
    handlerId: string;
  };
}

interface Subscriptions {
  [key: string]: {
    actionType: WSActionTypes;
    handlerId: string;
    trigger: WSMessageResolver<any>;
    subId: string;
  };
}

interface IWsToHttp {
  joinWaitingRoom: (nftId: string, bet: number) => Promise<any>;
  exitQueue: () => Promise<any>;
  makeMove: (card: CardNames) => Promise<any>;
  subscribeToEvent: <T>(actionType: WSActionTypes) => Promise<T>;
  unsubscribeFromEvent: (actionType: WSActionTypes) => void;
  getFightData: () => Promise<WSFightData>;
  giveUp: () => Promise<any>;
}

export const useWsToHttp = (): IWsToHttp => {
  const { sendWSMessage, addMessageHandler, removeMessageHandler } = useWs();
  const requests: Requests = {};
  const subscriptions: Subscriptions = {};

  const sendMessage = <T>(
    actionType: WSActionTypes,
    data: any
  ): SendMessageResult<T> => {
    const reqId = nanoid(16);
    const encoded = encodeWSRequest({
      actionType,
      data,
      token: localStorage.getToken(),
      reqId
    });
    sendWSMessage(encoded);
    let res, rej;
    const promise = new Promise<T>((resolve, reject) => {
      res = (data: T) => resolve(data);
      rej = (data) => reject(data);
    });
    return { promise, reqId, resolve: res, reject: rej };
  };

  const sendRequest = <T>(
    actionType: WSActionTypes,
    data: any,
    handler: WSMessageHandler<T>
  ): Promise<T> => {
    const { promise, reqId, resolve, reject } = sendMessage<T>(
      actionType,
      data
    );
    const handlerId = addMessageHandler<T>((parsed) =>
      commonHandler<T>(parsed, reqId, handler)
    );
    requests[reqId] = {
      resolve,
      reject,
      reqId,
      handlerId
    };
    return promise;
  };

  const joinWaitingRoom = (nftId: string, bet: number): Promise<any> =>
    sendRequest(
      wsActionTypes.client.joinWaitingRoom,
      { nftId, bet },
      joinWaitingRoomHandler
    );

  const exitQueue = (): Promise<any> =>
    sendRequest(wsActionTypes.client.exit, {}, exitHandler);

  const makeMove = (card: CardNames): Promise<any> =>
    sendRequest(wsActionTypes.client.makeMove, { card }, makeMoveHandler);

  const getFightData = (): Promise<WSFightData> =>
    sendRequest<WSFightData>(
      wsActionTypes.client.getFightData,
      null,
      getFightDataHandler
    );

  const giveUp = (): Promise<any> =>
    sendRequest(wsActionTypes.client.giveUp, null, giveUpHandler);

  const commonHandler = <T>(
    parsed: WSMessage<T>,
    reqId: string,
    handler: WSMessageHandler<T>
  ) => {
    if (parsed.reqId !== reqId) return;
    const request = requests[reqId];
    removeMessageHandler(request.handlerId);
    handler(parsed, request.resolve, request.reject);
  };

  //SUBSCRIPTIONS
  const subscribeToEvent = <T>(actionType: WSActionTypes): Promise<T> => {
    const subId = nanoid(16);
    let trigger;
    const promise = new Promise<T>((resolve) => {
      trigger = (data: T) => resolve(data);
    });
    const handlerId = addMessageHandler<T>((parsed) =>
      commonSubscriptionHandler<T>(parsed, subId)
    );
    subscriptions[subId] = {
      actionType,
      handlerId,
      trigger,
      subId
    };
    return promise;
  };

  const unsubscribeFromEvent = (actionType: WSActionTypes) => {
    const subs = Object.values(subscriptions).filter(
      (s) => s.actionType === actionType
    );
    if (subs.length > 0) {
      subs.forEach((sub) => {
        removeMessageHandler(sub.handlerId);
        delete subscriptions[sub.subId];
      });
    }
  };

  const commonSubscriptionHandler = <T>(
    parsed: WSMessage<T>,
    subId: string
  ) => {
    const { actionType, handlerId, trigger } = subscriptions[subId];
    if (parsed.actionType === actionType) {
      trigger(parsed.data);
      removeMessageHandler(handlerId);
    }
  };

  return {
    joinWaitingRoom,
    exitQueue,
    makeMove,
    subscribeToEvent,
    unsubscribeFromEvent,
    getFightData,
    giveUp
  };
};
